import type {
  DbLeadSourceInsert,
  DbLeadSourcePublic,
  LeadSource,
  SourceType,
} from "@/types/database";
import type { DbLeadSourceUiViewRow } from "@/services/lead-source.service";

/**
 * Minimal UI shape used when creating/updating a lead source.
 * Do not put analytics fields here as source-of-truth.
 */
type UiLeadSourceInput = {
  id?: string;
  name: string;
  sourceType: SourceType;
  organizationId?: string | null;
};

/**
 * Current public.lead_sources insert mapper
 * Aligned to the current schema:
 * - name
 * - source_type
 * - organization_id
 *
 * Analytics should come from views, not be inserted into lead_sources.
 */
export function mapLeadSourceToDbInsert(
  source: UiLeadSourceInput,
  organizationId: string
): DbLeadSourceInsert {
  return {
    organization_id: organizationId,
    name: source.name.trim(),
    source_type: source.sourceType,
  };
}

/**
 * Optional raw-table mapper.
 * Use only if you need to show/edit master source data from public.lead_sources.
 */
export function mapDbLeadSourceToUi(
  row: DbLeadSourcePublic,
): LeadSource {
  return {
    id: row.id,
    name: row.name,
    sourceType: row.source_type as SourceType,
    active: row.is_active,
    campaign: row.campaign ?? "",
    notes: "",
    totalLeads: 0,
    acceptedLeads: 0,
    totalJobs: 0,
    contacted: 0,
    appointmentsSet: 0,
    appointmentsCompleted: 0,
    connectionRate: 0,
    bookingRate: 0,
    showRate: 0,
    saleRate: 0,
    acceptanceRate: 0,
    rejectionRate: 0,
    monthlyCost: undefined,
  };
}

/**
 * Preferred mapper for the dashboard/list page.
 * Maps from public.v_lead_sources_ui to the existing LeadSource UI shape.
 */
export function mapDbLeadSourceToUiFromView(
  row: DbLeadSourceUiViewRow,
): LeadSource {
  const acceptedLeads = Number(row.accepted_leads ?? 0);

  return {
    id: row.id,
    name: row.name,
    sourceType: row.sourceType as SourceType,
    active: row.active,
    campaign: row.campaign ?? "",
    notes: row.notes ?? "",
    totalLeads: acceptedLeads,
    acceptedLeads,
    totalJobs: Number(row.total_jobs ?? 0),
    contacted: Number(row.contacted ?? 0),
    appointmentsSet: Number(row.appointmentsSet ?? 0),
    appointmentsCompleted: Number(row.appointmentsCompleted ?? 0),
    connectionRate: Number(row.connectionRate ?? 0),
    bookingRate: Number(row.bookingRate ?? 0),
    showRate: Number(row.showRate ?? 0),
    saleRate: Number(row.saleRate ?? 0),
    acceptanceRate: Number(row.acceptanceRate ?? 0),
    rejectionRate: Number(row.rejectionRate ?? 0),
    monthlyCost: undefined,
  };
}
